import { Component, EventEmitter, Output, OnInit , ChangeDetectorRef } from '@angular/core';
import { WizardStateService } from '../../../services/wizard-state.service';
import { ERA_FLAVORS } from '../../../constants/era-flavors.constants';
import { take } from 'rxjs/operators';

import { WorldEra } from '../../../models/wizard-state.model';

@Component({
    selector: 'app-world-identity-step',
    templateUrl: './world-identity-step.component.html',
    styleUrls: ['./world-identity-step.component.scss'],
    standalone: false
})
export class WorldIdentityStepComponent implements OnInit {
  @Output() next = new EventEmitter<void>();
  @Output() back = new EventEmitter<void>();

  flavors = ERA_FLAVORS;

  mapName = '';
  requiredYears = 1000; // %VAR: default fallback
  eras: WorldEra[] = [];

  // Validation tracking
  showErrorMsg = false;
  validationErrorText = '';
  showDeficiencyModal = false;

  constructor(private stateService: WizardStateService, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.stateService.state.pipe(take(1)).subscribe(state => {
      this.mapName = state.mapName || 'World';

      // Determine required years (%VAR) from prefilled state settings
      if (state.minYearsRequired) {
        this.requiredYears = state.minYearsRequired;
      }

      // Restore eras if saved, else initialize defaults
      if (state.worldIdentity && state.worldIdentity.eras && state.worldIdentity.eras.length > 0) {
        this.eras = JSON.parse(JSON.stringify(state.worldIdentity.eras));
      } else {
        // Initialize default eras
        let defaultCurrentEraName = 'Age of Conquest';
        let defaultCurrentEraYears = 1200;

        if (state.currentEra) {
          defaultCurrentEraName = state.currentEra.name || defaultCurrentEraName;
          defaultCurrentEraYears = state.currentEra.year || defaultCurrentEraYears;
        }

        this.eras = [
          {
            type: 'Current',
            name: defaultCurrentEraName,
            years: defaultCurrentEraYears,
            flavor: '',
            lockedName: true,
            lockedYears: true
          },
          {
            type: 'Historical',
            name: '',
            years: null,
            flavor: '',
            lockedName: false,
            lockedYears: false
          }
        ];
      }
      this.cdr.detectChanges();
    });
  }

  /** Mirrors the backend's total-years formula: (current_year + 1) + sum of historical era spans. */
  get totalYears(): number {
    const currentEra = this.eras.find(era => era.type === 'Current');
    const currentYear = currentEra ? (currentEra.years || 0) : 0;
    const historicalSpan = this.eras
      .filter(era => era.type !== 'Current')
      .reduce((sum, era) => sum + (era.years || 0), 0);
    return (currentYear + 1) + historicalSpan;
  }

  get isYearsDeficient(): boolean {
    return this.totalYears < this.requiredYears;
  }

  addEra(): void {
    this.eras.push({
      type: 'Historical',
      name: '',
      years: null,
      flavor: '',
      lockedName: false,
      lockedYears: false
    });
  }

  removeEra(index: number): void {
    // Only allow removing optional historical eras (index >= 2)
    if (index >= 2) {
      this.eras.splice(index, 1);
    }
  }

  /** A historical row with no name, years, or flavor entered is simply unused — not required. */
  eraHasAnyInput(era: WorldEra): boolean {
    return !!era.name?.trim() || (era.years !== null && era.years > 0) || !!era.flavor;
  }

  onNext(): void {
    this.showErrorMsg = false;
    this.validationErrorText = '';

    for (const era of this.eras) {
      // Current era is locked/prefilled from the JSON — only its Flavor needs picking.
      if (era.type === 'Current') {
        if (!era.flavor) {
          this.validationErrorText = `Era Flavor is required for the current era.`;
          this.showErrorMsg = true;
          return;
        }
        continue;
      }

      // Untouched historical rows are optional — skip them entirely.
      if (!this.eraHasAnyInput(era)) {
        continue;
      }

      // Once a historical row has been started, it must be fully completed.
      if (!era.name.trim()) {
        this.validationErrorText = `Era Name is required for any era you've started filling in.`;
        this.showErrorMsg = true;
        return;
      }
      if (era.years === null || era.years <= 0) {
        this.validationErrorText = `Valid Year duration is required for any era you've started filling in.`;
        this.showErrorMsg = true;
        return;
      }
      if (!era.flavor) {
        this.validationErrorText = `Era Flavor is required for Era: "${era.name}".`;
        this.showErrorMsg = true;
        return;
      }
    }

    // Validate total years >= requiredYears
    if (this.isYearsDeficient) {
      this.showDeficiencyModal = true;
      return;
    }

    // Save and emit — drop untouched historical rows, no point persisting empty eras
    const eras = this.eras.filter(era => era.type === 'Current' || this.eraHasAnyInput(era));
    this.stateService.updateState({
      worldIdentity: { eras }
    });

    this.next.emit();
  }

  onBack(): void {
    this.back.emit();
  }
}
